import { createTheme } from '@mui/material/styles';
import adminTheme from './adminTheme';

/**
 * Créer un thème personnalisé basé sur les préférences utilisateur
 *
 * @param {Object} preferences - Préférences de thème de l'utilisateur
 * @returns {Object} - Thème MUI personnalisé
 */
export const createCustomTheme = (preferences) => {
    if (!preferences) {
        return adminTheme;
    }

    const { theme, colors } = preferences;

    // Si le thème est "light" ou "dark", utiliser le thème admin avec le mode approprié
    if (theme === 'light' || theme === 'dark') {
        return createTheme({
            ...adminTheme,
            palette: {
                ...adminTheme.palette,
                mode: theme,
            },
        });
    }

    // Si le thème est "custom", créer un thème personnalisé avec les couleurs spécifiées
    if (theme === 'custom' && colors) {
        return createTheme({
            ...adminTheme,
            palette: {
                ...adminTheme.palette,
                primary: {
                    main: colors.primary || adminTheme.palette.primary.main,
                    light: lightenColor(colors.primary, 0.2) || adminTheme.palette.primary.light,
                    dark: darkenColor(colors.primary, 0.2) || adminTheme.palette.primary.dark,
                    contrastText: getContrastText(colors.primary) || adminTheme.palette.primary.contrastText,
                },
                secondary: {
                    main: colors.secondary || adminTheme.palette.secondary.main,
                    light: lightenColor(colors.secondary, 0.2) || adminTheme.palette.secondary.light,
                    dark: darkenColor(colors.secondary, 0.2) || adminTheme.palette.secondary.dark,
                    contrastText: getContrastText(colors.secondary) || adminTheme.palette.secondary.contrastText,
                },
                success: {
                    main: colors.success || adminTheme.palette.success.main,
                    light: lightenColor(colors.success, 0.2) || adminTheme.palette.success.light,
                    dark: darkenColor(colors.success, 0.2) || adminTheme.palette.success.dark,
                    contrastText: getContrastText(colors.success) || adminTheme.palette.success.contrastText,
                },
                warning: {
                    main: colors.warning || adminTheme.palette.warning.main,
                    light: lightenColor(colors.warning, 0.2) || adminTheme.palette.warning.light,
                    dark: darkenColor(colors.warning, 0.2) || adminTheme.palette.warning.dark,
                    contrastText: getContrastText(colors.warning) || adminTheme.palette.warning.contrastText,
                },
                error: {
                    main: colors.error || adminTheme.palette.error.main,
                    light: lightenColor(colors.error, 0.2) || adminTheme.palette.error.light,
                    dark: darkenColor(colors.error, 0.2) || adminTheme.palette.error.dark,
                    contrastText: getContrastText(colors.error) || adminTheme.palette.error.contrastText,
                },
                info: {
                    main: colors.info || adminTheme.palette.info.main,
                    light: lightenColor(colors.info, 0.2) || adminTheme.palette.info.light,
                    dark: darkenColor(colors.info, 0.2) || adminTheme.palette.info.dark,
                    contrastText: getContrastText(colors.info) || adminTheme.palette.info.contrastText,
                },
                dashboard: {
                    users: colors.primary || adminTheme.palette.dashboard.users,
                    categories: colors.secondary || adminTheme.palette.dashboard.categories,
                    properties: colors.success || adminTheme.palette.dashboard.properties,
                    roles: colors.warning || adminTheme.palette.dashboard.roles,
                    permissions: colors.error || adminTheme.palette.dashboard.permissions,
                    verifications: colors.info || adminTheme.palette.dashboard.verifications,
                },
            },
        });
    }

    // Par défaut, retourner le thème admin
    return adminTheme;
};

/**
 * Éclaircir une couleur
 *
 * @param {string} color - Couleur hexadécimale
 * @param {number} amount - Quantité d'éclaircissement (0-1)
 * @returns {string} - Couleur éclaircie
 */
const lightenColor = (color, amount) => {
    if (!color) return null;

    try {
        // Convertir la couleur hexadécimale en RGB
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        // Éclaircir la couleur
        const lightenR = Math.round(r + (255 - r) * amount);
        const lightenG = Math.round(g + (255 - g) * amount);
        const lightenB = Math.round(b + (255 - b) * amount);

        // Convertir en hexadécimal
        return `#${lightenR.toString(16).padStart(2, '0')}${lightenG.toString(16).padStart(2, '0')}${lightenB.toString(16).padStart(2, '0')}`;
    } catch (error) {
        console.error('Error lightening color:', error);
        return color;
    }
};

/**
 * Assombrir une couleur
 *
 * @param {string} color - Couleur hexadécimale
 * @param {number} amount - Quantité d'assombrissement (0-1)
 * @returns {string} - Couleur assombrie
 */
const darkenColor = (color, amount) => {
    if (!color) return null;

    try {
        // Convertir la couleur hexadécimale en RGB
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        // Assombrir la couleur
        const darkenR = Math.round(r * (1 - amount));
        const darkenG = Math.round(g * (1 - amount));
        const darkenB = Math.round(b * (1 - amount));

        // Convertir en hexadécimal
        return `#${darkenR.toString(16).padStart(2, '0')}${darkenG.toString(16).padStart(2, '0')}${darkenB.toString(16).padStart(2, '0')}`;
    } catch (error) {
        console.error('Error darkening color:', error);
        return color;
    }
};

/**
 * Obtenir la couleur de texte contrastée (blanc ou noir)
 *
 * @param {string} color - Couleur hexadécimale
 * @returns {string} - Couleur de texte contrastée (#ffffff ou #000000)
 */
const getContrastText = (color) => {
    if (!color) return null;

    try {
        // Convertir la couleur hexadécimale en RGB
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        // Calculer la luminosité (formule YIQ)
        const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

        // Retourner blanc ou noir en fonction de la luminosité
        return (yiq >= 128) ? '#000000' : '#ffffff';
    } catch (error) {
        console.error('Error calculating contrast text:', error);
        return '#ffffff';
    }
};

/**
 * Gestionnaire de thèmes
 */
const themeManager = {
    createCustomTheme,
};

export default themeManager;